import { Injectable, computed, inject, signal } from '@angular/core';

import type { CreateProjectDialogSubmitResult } from '@features/kt/projects/components/create-project-dialog/create-project-dialog';
import { ProjectsApiError, ProjectsApiService } from '@features/kt/projects/services/api/projects.api.service';

import type { ProjectDetail, ProjectListItem } from '@api';

const HTTP_BAD_REQUEST = 400;
const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;

/** D2: (a) no membership at all, (b) memberships but no open project, (c) an open project. */
export type ActivityProjectPanelState = 'assign' | 'library' | 'project';

export type RemoveFromOpenProjectResult = 'error' | 'gone' | 'ok';
export type RenameOpenProjectResult = 'conflict' | 'error' | 'ok';

/**
 * Panel-scoped state for the activity editor's project column (KT-198.2 / D2).
 *
 * Deliberately NOT the global `ProjectsStore` (T-3): that store's `#detail` signal, its
 * deferred list reloads and its drag state belong to the project detail page. This one is
 * provided at the edit route (KT-200.5) so the edit page and panel share one instance.
 */
// eslint-disable-next-line @angular-eslint/use-injectable-provided-in -- intentionally panel-scoped (T-3), not tree-shakable-root
@Injectable()
export class ActivityProjectContextStore {
  readonly #api = inject(ProjectsApiService);

  readonly #memberships = signal<readonly ProjectListItem[]>([]);
  readonly #openProject = signal<ProjectDetail | null>(null);
  readonly #isLoading = signal(false);
  readonly #error = signal<unknown>(null);

  /**
   * The brief's Key Decision: the "also in these projects" block mirrors the ACTIVITY's
   * memberships minus the open project — not the project's own neighbours. A freshly created,
   * single-membership activity therefore has an empty block, by design.
   */
  readonly otherProjects = computed<readonly ProjectListItem[]>(() => {
    const openProjectId = this.#openProject()?.id;

    return openProjectId === undefined
      ? this.#memberships()
      : this.#memberships().filter((project) => project.id !== openProjectId);
  });
  /** Total project membership count (`N`) from `GET /v1/projects?activityId=`. */
  readonly totalProjectCount = computed(() => this.#memberships().length);
  /**
   * Other-project count for panel warning copy (`N − 1`). Uses total membership length, never
   * `otherProjects.length` (S6 — library entry without `?projectId=`).
   */
  readonly otherProjectCount = computed(() => Math.max(0, this.totalProjectCount() - 1));
  /**
   * `true` when membership is loaded, the list call did not fail, and `N ≥ 2` (C1).
   * Hidden while loading or on API error (S3, S4).
   */
  readonly isMultiProject = computed(
    () => !this.#isLoading() && this.#error() === null && this.totalProjectCount() >= 2,
  );
  readonly state = computed<ActivityProjectPanelState>(() => {
    if (this.#openProject() !== null) {
      return 'project';
    }

    return this.#memberships().length > 0 ? 'library' : 'assign';
  });
  /**
   * Stale-while-revalidate: only the FIRST load blanks the panel for a spinner, because there
   * is genuinely nothing to show yet (and the `'assign'` surface must not flash there). Every
   * later load — switching activity inside the open project, a post-mutation refresh — keeps
   * the current content on screen and swaps it when the new data lands, so the column does not
   * flicker on every table-of-contents click.
   */
  readonly isInitialLoading = computed(
    () => this.#isLoading() && this.#memberships().length === 0 && this.#openProject() === null,
  );

  /** `GET /v1/projects?activityId=` — every project the edited activity belongs to. */
  readonly memberships = this.#memberships.asReadonly();
  /** `GET /v1/projects/{projectId}` — the project the editor was opened from, if any. */
  readonly openProject = this.#openProject.asReadonly();
  readonly isLoading = this.#isLoading.asReadonly();
  readonly error = this.#error.asReadonly();
  /**
   * Bumped by every visible {@link load}. Mutations and silent loads capture it and skip their state
   * writes once it moved, so a response that lands after the editor switched activity cannot write
   * stale data.
   */
  #generation = 0;
  #mutationCount = 0;
  #mutationsInFlight = 0;
  #silentLoadOwed = false;
  #silentLoadSeq = 0;
  #activityId: string | null = null;
  #projectId: string | null = null;
  /**
   * D5: the project created by a `createProjectWith` whose `addActivities` step then failed.
   * The next submit repeats ONLY the add, so retrying never leaves a second project behind.
   */
  #pendingProjectId: string | null = null;

  /**
   * Loads both halves of the panel in parallel (`Promise.allSettled`, so a failing project
   * read cannot swallow the membership list). Only a failing membership list raises
   * {@link error} — a failing project read degrades to the `'library'` state instead of
   * blanking the panel.
   *
   * @param activityId - the activity being edited.
   * @param projectId - the open project from `?projectId=`, or `null`.
   * @param options - load options.
   * @param options.silent - skips the loading flag (so the multi-project warning stays) and keeps the current content on a non-404 failure.
   */
  async load(activityId: string, projectId: string | null, options: { silent?: boolean } = {}): Promise<void> {
    const silent = options.silent === true && !this.#isLoading();
    this.#activityId = activityId;
    this.#projectId = projectId;
    if (silent && this.#mutationsInFlight > 0) {
      this.#silentLoadOwed = true;
      return;
    }

    const mutationCount = this.#mutationCount;
    const isCurrent = silent ? this.#claimSilentLoad() : this.#claimVisibleLoad();

    const [membershipsResult, openProjectResult] = await Promise.allSettled([
      this.#api.list({ activityId }),
      projectId === null ? Promise.resolve(null) : this.#api.detail(projectId),
    ]);

    if (silent && mutationCount !== this.#mutationCount) {
      this.#silentLoadOwed = true;
      this.#runOwedSilentLoad();
      return;
    }

    if (!isCurrent()) {
      return;
    }

    if (membershipsResult.status === 'fulfilled') {
      this.#memberships.set([...(membershipsResult.value.items ?? [])]);
      this.#error.set(null);
    } else if (!silent) {
      this.#memberships.set([]);
      this.#error.set(membershipsResult.reason);
    }

    if (openProjectResult.status === 'fulfilled') {
      this.#openProject.set(openProjectResult.value);
    } else if (!silent || statusOf(openProjectResult.reason) === HTTP_NOT_FOUND) {
      this.#openProject.set(null);
    }

    if (!silent) {
      this.#isLoading.set(false);
    }
  }

  /**
   * D5 — the two existing endpoints, one after the other: `POST /v1/projects` then
   * `POST /v1/projects/{id}/activities`. A failure in step 1 created nothing; a failure in
   * step 2 leaves a legitimate empty project, remembered as {@link #pendingProjectId} so the
   * teacher's retry only repeats the add and never creates a second project.
   *
   * On that retry the submitted name and description are written onto the half-created project
   * first, so an edited name can never be silently dropped. A `404` anywhere on the pending
   * path means the project is gone: the pending id is dropped so the next submit starts over
   * instead of retrying a dead id forever.
   *
   * @param name - the new project's name.
   * @param description - the optional description.
   * @param activityId - the activity to put into the new project.
   * @returns the `CreateProjectDialog` callback union.
   */
  createProjectWith(
    name: string,
    description: string | undefined,
    activityId: string,
  ): Promise<CreateProjectDialogSubmitResult> {
    return this.#mutate(() => this.#createProjectWith(name, description, activityId));
  }

  /**
   * Forgets a half-created project, so a later submit starts a fresh one. The panel calls this
   * when the create dialog closes: an abandoned empty project must never adopt the next
   * activity the teacher decides to file.
   */
  discardPendingProject(): void {
    this.#pendingProjectId = null;
  }

  /**
   * D12 — the teacher opened a library TEMPLATE row of the project, so the editor forked a
   * private copy: the project must now hold that copy instead of the template. Two existing
   * endpoints, in this order, so the project is never left without the activity:
   * `POST …/activities` with the fork, then `DELETE …/activities/{templateOccurrenceId}`.
   *
   * Only the clicked occurrence is replaced — the same template may sit in the project more
   * than once, and the others are not what the teacher opened.
   *
   * @param projectId - the open project.
   * @param templateOccurrenceId - the clicked template occurrence to break.
   * @param forkedActivityId - the freshly forked own instance to put in its place.
   * @returns `'ok'` when both writes landed, `'partial'` when the fork is in but the template
   * row could not be broken (both rows are visible until it is retried), `'error'` when nothing
   * changed.
   */
  swapTemplateOccurrence(
    projectId: string,
    templateOccurrenceId: string,
    forkedActivityId: string,
  ): Promise<'error' | 'ok' | 'partial'> {
    return this.#mutate(() => this.#swapTemplateOccurrence(projectId, templateOccurrenceId, forkedActivityId));
  }

  /**
   * `PATCH /v1/projects/{id}/name` for the open project.
   *
   * @param value - the finalized project name.
   * @returns `'conflict'` on a `409` (the caller snackbars and reloads), `'error'` on
   * anything else, `'ok'` on success.
   */
  renameOpenProject(value: string): Promise<RenameOpenProjectResult> {
    return this.#mutate(() => this.#renameOpenProject(value));
  }

  /**
   * `DELETE /v1/projects/{id}/activities/{projectActivityId}`.
   *
   * @param projectActivityId - the occurrence to break (not the activity id).
   * @returns `'gone'` on a `404` — the membership was already absent, so the panel reloaded
   * itself instead of failing — `'error'` on any other failure, `'ok'` on success.
   */
  removeFromOpenProject(projectActivityId: string): Promise<RemoveFromOpenProjectResult> {
    return this.#mutate(() => this.#removeFromOpenProject(projectActivityId));
  }

  async #mutate<T>(run: () => Promise<T>): Promise<T> {
    this.#mutationCount++;
    this.#mutationsInFlight++;
    try {
      return await run();
    } finally {
      this.#mutationsInFlight--;
      this.#runOwedSilentLoad();
    }
  }

  #runOwedSilentLoad(): void {
    if (!this.#silentLoadOwed || this.#mutationsInFlight > 0 || this.#activityId === null) {
      return;
    }

    this.#silentLoadOwed = false;
    void this.load(this.#activityId, this.#projectId, { silent: true });
  }

  async #createProjectWith(
    name: string,
    description: string | undefined,
    activityId: string,
  ): Promise<CreateProjectDialogSubmitResult> {
    const generation = this.#generation;
    const pendingProjectId = this.#pendingProjectId;
    let projectId: string;

    if (pendingProjectId === null) {
      try {
        const created = await this.#api.create(description === undefined ? { name } : { name, description });
        projectId = created.id;
        this.#pendingProjectId = projectId;
      } catch (error) {
        return toSubmitError(error);
      }
    } else {
      projectId = pendingProjectId;

      const syncError = await this.#applySubmittedFields(projectId, name, description);
      if (syncError !== null) {
        return syncError;
      }
    }

    try {
      const detail = await this.#api.addActivities(projectId, [activityId]);
      this.#pendingProjectId = null;

      if (generation === this.#generation) {
        this.#openProject.set(detail);
        this.#projectId = detail.id;
        await this.#reloadMemberships(activityId, generation);
      }

      return { status: 'success' };
    } catch (error) {
      if (statusOf(error) === HTTP_NOT_FOUND) {
        this.#pendingProjectId = null;
      }

      return { status: 'error', kind: 'other' };
    }
  }

  async #swapTemplateOccurrence(
    projectId: string,
    templateOccurrenceId: string,
    forkedActivityId: string,
  ): Promise<'error' | 'ok' | 'partial'> {
    try {
      await this.#api.addActivities(projectId, [forkedActivityId]);
    } catch {
      return 'error';
    }

    try {
      await this.#api.removeActivity(projectId, templateOccurrenceId);
    } catch (error) {
      // A `404` means that occurrence is already gone (a double navigation, or another device
      // did it) — the end state is the intended one, so it is not a failure.
      return statusOf(error) === HTTP_NOT_FOUND ? 'ok' : 'partial';
    }

    return 'ok';
  }

  async #renameOpenProject(value: string): Promise<RenameOpenProjectResult> {
    const project = this.#openProject();
    if (project === null) {
      return 'error';
    }

    const generation = this.#generation;

    try {
      const detail = await this.#api.updateName(project.id, value);

      if (generation === this.#generation) {
        this.#openProject.set(detail);
      }

      return 'ok';
    } catch (error) {
      return statusOf(error) === HTTP_CONFLICT ? 'conflict' : 'error';
    }
  }

  async #removeFromOpenProject(projectActivityId: string): Promise<RemoveFromOpenProjectResult> {
    const project = this.#openProject();
    const activityId = this.#activityId;
    if (project === null || activityId === null) {
      return 'error';
    }

    const generation = this.#generation;

    try {
      const detail = await this.#api.removeActivity(project.id, projectActivityId);

      if (generation === this.#generation) {
        this.#openProject.set(detail);
        await this.#reloadMemberships(activityId, generation);
      }

      return 'ok';
    } catch (error) {
      if (statusOf(error) !== HTTP_NOT_FOUND) {
        return 'error';
      }

      if (generation === this.#generation) {
        await this.load(activityId, this.#projectId);
      }

      return 'gone';
    }
  }

  #claimVisibleLoad(): () => boolean {
    const generation = ++this.#generation;
    // A half-created project belongs to the context it was started in. The panel survives an
    // in-place `:id` swap (the editor keeps `isLoaded()` true), so carrying it over would put
    // the NEXT activity into the previous activity's project.
    this.#pendingProjectId = null;
    this.#isLoading.set(true);
    this.#error.set(null);

    return () => generation === this.#generation;
  }

  #claimSilentLoad(): () => boolean {
    const generation = this.#generation;
    const silentLoadSeq = ++this.#silentLoadSeq;

    return () => generation === this.#generation && silentLoadSeq === this.#silentLoadSeq;
  }

  /**
   * Writes the freshly submitted name and description onto a half-created project, using the
   * existing `PATCH …/name` and `PATCH …/description` endpoints. A retryable failure keeps the
   * pending id (so the retry still cannot duplicate the project); a `404` drops it, because the
   * project no longer exists and every further submit would hit the same `404`.
   *
   * @param projectId - the half-created project.
   * @param name - the submitted name.
   * @param description - the submitted description, `undefined` when the field was left empty.
   * @returns `null` when both writes landed, otherwise the submit error to answer the dialog with.
   */
  async #applySubmittedFields(
    projectId: string,
    name: string,
    description: string | undefined,
  ): Promise<CreateProjectDialogSubmitResult | null> {
    try {
      await this.#api.updateName(projectId, name);
      await this.#api.updateDescription(projectId, description ?? null);

      return null;
    } catch (error) {
      if (statusOf(error) === HTTP_NOT_FOUND) {
        this.#pendingProjectId = null;
      }

      return toSubmitError(error);
    }
  }

  async #reloadMemberships(activityId: string, generation: number): Promise<void> {
    try {
      const response = await this.#api.list({ activityId });

      if (generation === this.#generation) {
        this.#memberships.set([...(response.items ?? [])]);
      }
    } catch {
      // The mutation itself succeeded and `openProject` already carries its result; a failed
      // membership refresh only leaves the side lists one step stale, so it must not surface
      // as a failed mutation.
    }
  }
}

/**
 * @param error - a rejected {@link ProjectsApiService} call's reason.
 * @returns its HTTP status, or `0` when the failure carries none.
 */
function statusOf(error: unknown): number {
  return error instanceof ProjectsApiError ? error.status : 0;
}

/**
 * @param error - a rejected create / patch call's reason.
 * @returns the dialog's error result — `400` is the inline validation message, everything else
 * the general one.
 */
function toSubmitError(error: unknown): CreateProjectDialogSubmitResult {
  return { status: 'error', kind: statusOf(error) === HTTP_BAD_REQUEST ? 'validation' : 'other' };
}
